import { spring, springs, prefersReducedMotion } from './engine';

// Ready-made effects on top of the spring engine. They write styles directly (no React re-renders)
// and clean up after themselves. Starting a new effect on an element stops the previous one.
//
// A visual state is { x, y, scale, opacity } relative to the element's natural look.
// x and y are pixels, or strings like '100%' (of the element's own width/height).

const running = new WeakMap(); // element -> controller
const current = new WeakMap(); // element -> last applied state (resolved to pixels)

function claim(el, controller) {
  running.get(el)?.stop();
  running.set(el, controller);
}

function resolve(el, s = {}) {
  const px = (v, size) => (typeof v === 'string' && v.endsWith('%') ? (parseFloat(v) / 100) * size : Number(v) || 0);
  return {
    x: px(s.x ?? 0, el.offsetWidth),
    y: px(s.y ?? 0, el.offsetHeight),
    scale: s.scale ?? 1,
    opacity: s.opacity ?? 1,
  };
}

const isNatural = (s) => s.x === 0 && s.y === 0 && s.scale === 1 && s.opacity >= 1;

function apply(el, s) {
  current.set(el, s);
  el.style.transform = s.x === 0 && s.y === 0 && s.scale === 1 ? '' : `translate3d(${s.x}px, ${s.y}px, 0) scale(${s.scale})`;
  el.style.opacity = s.opacity >= 1 ? '' : String(Math.max(0, s.opacity));
}

const mix = (a, b, t) => a + (b - a) * t;

// The state an element is shown in right now (natural if nothing animates it)
export const currentState = (el) => current.get(el) || {};

// Animates elements between two states, all driven by one shared spring, so they move as one.
//   targets [{ el, from, to, origin }]  missing from/to mean the natural state
export function transition(targets, { config = springs.snappy, onDone } = {}) {
  const list = targets.filter(t => t.el).map(t => ({ el: t.el, a: resolve(t.el, t.from), b: resolve(t.el, t.to), origin: t.origin }));
  if (!list.length) { onDone?.(); return null; }
  for (const t of list) {
    if (t.origin) t.el.style.transformOrigin = t.origin;
    t.el.style.willChange = 'transform, opacity';
  }
  const c = spring({
    from: 0,
    to: 1,
    config,
    precision: 0.002,
    onUpdate: (p) => {
      for (const t of list) {
        apply(t.el, {
          x: mix(t.a.x, t.b.x, p),
          y: mix(t.a.y, t.b.y, p),
          scale: mix(t.a.scale, t.b.scale, p),
          // overshoot may move things past their target, but never more than fully visible
          opacity: Math.min(1, mix(t.a.opacity, t.b.opacity, p)),
        });
      }
    },
    onComplete: () => {
      for (const t of list) {
        t.el.style.willChange = '';
        if (isNatural(t.b)) {
          t.el.style.transformOrigin = '';
          current.delete(t.el);
        }
        if (running.get(t.el) === c) running.delete(t.el);
      }
      onDone?.();
    },
  });
  for (const t of list) claim(t.el, c);
  return c;
}

// Element springs in from a slightly smaller, lower, transparent state.
//   from   start state (default: 12px lower, 85% size, invisible)
//   origin transform-origin, e.g. 'bottom right' for own chat bubbles
export function popIn(el, { from = {}, origin = 'center', config = springs.bouncy, onDone } = {}) {
  const { x = 0, y = 12, scale = 0.85, opacity = 0 } = from;
  return transition([{ el, from: { x, y, scale, opacity }, origin }], { config, onDone });
}

/* ── Exits ── */

let suppressUntil = 0;
// Skips exit animations for a moment, e.g. while a page change removes a whole screen at once
export function suppressExits(ms = 150) {
  suppressUntil = performance.now() + ms;
}

// A non-interactive copy of an element at the same place on screen. It lives in document.body
// (position fixed), or in `parent` (position absolute), before or after the other children.
function makeGhost(el, { parent = null, below = false, zIndex } = {}) {
  const saved = el.style.transform;
  el.style.transform = 'none'; // measure the layout box; the copy gets the transform back
  const rect = el.getBoundingClientRect();
  el.style.transform = saved;
  const cs = getComputedStyle(el);

  const ghost = el.cloneNode(true);
  // Form fields keep their typed values, scroll containers their position
  const fields = el.querySelectorAll('input, textarea, select');
  ghost.querySelectorAll('input, textarea, select').forEach((f, i) => { if (fields[i]) f.value = fields[i].value; });
  ghost.removeAttribute('id');
  ghost.setAttribute('aria-hidden', 'true');
  ghost.inert = true;

  let left = rect.left;
  let top = rect.top;
  if (parent) {
    const p = parent.getBoundingClientRect();
    left -= p.left;
    top -= p.top;
  }
  Object.assign(ghost.style, {
    position: parent ? 'absolute' : 'fixed',
    left: `${left}px`,
    top: `${top}px`,
    right: 'auto',
    bottom: 'auto',
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    margin: '0',
    boxSizing: 'border-box',
    pointerEvents: 'none',
    zIndex: String(zIndex ?? (cs.zIndex === 'auto' ? 9999 : cs.zIndex)),
    // inherited text styles would otherwise come from <body>
    color: cs.color,
    fontFamily: cs.fontFamily,
    fontSize: cs.fontSize,
    lineHeight: cs.lineHeight,
  });
  // Scrolled containers inside (e.g. a chat's message list) keep their position in the copy
  const scrolled = [];
  [el, ...el.querySelectorAll('*')].forEach((n, i) => { if (n.scrollTop || n.scrollLeft) scrolled.push([i, n.scrollTop, n.scrollLeft]); });
  const host = parent || document.body;
  if (below) host.insertBefore(ghost, host.firstChild);
  else host.appendChild(ghost);
  if (scrolled.length) {
    const copies = [ghost, ...ghost.querySelectorAll('*')];
    for (const [i, top, left] of scrolled) { if (copies[i]) { copies[i].scrollTop = top; copies[i].scrollLeft = left; } }
  }
  return ghost;
}

// Plays an exit for an element React is about to remove: a copy animates from the element's
// current state to `to`. parts: { selector: state } animates matching children separately
// (e.g. a backdrop fades while the sheet in it slides away). force ignores suppressExits().
export function exitGhost(el, { to, parts = {}, config = springs.exit, force = false, parent, below, zIndex } = {}) {
  if (!el?.isConnected || prefersReducedMotion() || (!force && performance.now() < suppressUntil)) return null;
  const ghost = makeGhost(el, { parent, below, zIndex });
  const targets = [];
  if (to) targets.push({ el: ghost, from: currentState(el), to, origin: el.style.transformOrigin || undefined });
  for (const [selector, partTo] of Object.entries(parts)) {
    const orig = el.matches(selector) ? el : el.querySelector(selector);
    const copy = ghost.matches(selector) ? ghost : ghost.querySelector(selector);
    if (orig && copy && partTo) targets.push({ el: copy, from: currentState(orig), to: partTo, origin: orig.style.transformOrigin || undefined });
  }
  return transition(targets, { config, onDone: () => ghost.remove() });
}

// Shared-element flight: the element starts offset by (dx, dy) from where it really is and flies
// to its place. A copy flies above everything (so scroll containers can't clip it) while the real
// element stays hidden. The copy follows the real element live, so scrolling or a parent that is
// itself animating is handled. Children marked data-fly-hide are left out of the copy.
export function flyFrom(el, { dx = 0, dy = 0, config = springs.send, zIndex = 450, onDone } = {}) {
  if (!el) return null;
  const start = el.getBoundingClientRect();
  const ghost = el.cloneNode(true);
  ghost.querySelectorAll('[data-fly-hide]').forEach(n => n.remove());
  ghost.removeAttribute('id');
  ghost.setAttribute('aria-hidden', 'true');
  Object.assign(ghost.style, {
    position: 'fixed',
    left: `${start.left}px`,
    top: `${start.top}px`,
    width: `${start.width}px`,
    margin: '0',
    pointerEvents: 'none',
    zIndex: String(zIndex),
    willChange: 'transform',
    transform: `translate3d(${dx}px, ${dy}px, 0)`,
  });
  document.body.appendChild(ghost);
  el.style.visibility = 'hidden';

  const cleanup = () => {
    ghost.remove();
    el.style.visibility = '';
    if (running.get(el) === controller) running.delete(el);
  };
  let c = null; // read in onUpdate, which also runs once before spring() returns
  c = spring({
    from: 1,
    to: 0,
    config,
    precision: 0.002,
    onUpdate: (p) => {
      if (!el.isConnected) { c?.stop(); cleanup(); return; }
      const now = el.getBoundingClientRect();
      ghost.style.transform = `translate3d(${now.left - start.left + dx * p}px, ${now.top - start.top + dy * p}px, 0)`;
    },
    onComplete: () => { cleanup(); onDone?.(); },
  });
  const controller = { stop: () => { c.stop(); cleanup(); return controller; }, finish: () => c.finish() };
  claim(el, controller);
  return controller;
}
